import { Entity, LinkableEntity } from '../entity';
import { AccountingSystem } from '../accounting/accounting.enums';
import { ActivityEntityName } from '../shared/activity.entity.name';
import { Address } from '../shared/address';
import { SiteTiny } from '../shared/site';
import { TaxRate } from '../taxrate/tax.rate';
import { NotifiableContactEntity } from '../contact/contact.options';
import { Attachment } from '../shared/attachment';
import { PurchaseOrderLine, TaxInclusiveOption } from './purchase.order.line';

export enum PurchaseOrderStatus {
    Draft = 'Draft',

    /** @deprecated never used */
    Submitted = 'Submitted',

    PendingApproval = 'Pending Approval',

    ChangesRequired = 'Changes Required',

    AwaitingDelivery = 'Awaiting Delivery',

    /** @deprecated use Approved */
    Authorised = 'Authorised',
    Approved = 'Approved',

    /** @deprecated use Billed */
    Reconciled = 'Reconciled',
    Billed = 'Billed',

    /** @deprecated use Billed */
    Error = 'Error',

    /** @deprecated use Pending Approval */
    SubmittedForApproval = 'Submitted For Approval',

    /** @deprecated use Cancelled */
    Void = 'Void',
    Cancelled = 'Cancelled',
}

export enum PurchaseOrderViewType {
    Job = 'Job',
    Global = 'Global'
}

export enum QuickbookTaxInclusiveOption {
    Exclusive = 'TaxExcluded',
    Inclusive = 'TaxInclusive',
    NoTax = 'NotApplicable'
}

export type PurchaseOrderDeliverOptions = 'pickup' | 'company' | 'job' | 'other';

export type PurchaseOrderKind = ActivityEntityName.Job | ActivityEntityName.Item
    | ActivityEntityName.Work | ActivityEntityName.WorkItem;

export interface PurchaseOrder extends LinkableEntity {

    poNumber?: string;
    poDate?: Date;
    deliveryDate?: Date;
    deliveryInstructions?: string;
    billingCompany: PurchaseOrderBillingCompany;
    reference?: string;
    supplier?: PurchaseOrderSupplier;
    comment?: string;
    attachments?: Attachment[];
    status: PurchaseOrderStatus;
    lines?: PurchaseOrderLine[];
    job?: Entity;
    createdBy?: Entity;
    owner?: Entity;

    deliveryOptions?: PurchaseOrderDeliverOptions;
    deliveryAddress?: DeliveryAddress;
    taxInclusiveOption: TaxInclusiveOption;
    subTotal?: number;
    total?: number;
    tax?: number;

    createdAt?: Date;
    updatedAt?: Date;

    theme?: Entity;

    priceBook?: Entity;

    pdf?: string;

    changesRequiredNote?: string;

    /**
     * @description
     * - If the purchase order created from Job-Item, it will be **Item** kind.
     * - If the purchase order created from Job > PO tab, it will be **Job** kind.
     * - etc.
     */
    kind?: PurchaseOrderKind;

    /** @deprecated */
    supplierMarkup?: number;

    /** @deprecated */
    taxRate?: TaxRate;

    /** @deprecated */
    chargeable?: boolean;

    /** @deprecated */
    accounting?: PurchaseOrderAccounting; // Purchase Order Accounting is belong to company therefore if company have many accounting, the integration will in Array for tracking one by one

    timezone?: string;
}

export interface PurchaseOrderAccounting {
    type: AccountingSystem;
    poAccountingId: string;
    deepLink?: string;
    success?: boolean;
    error?: any;
}


export interface DeliveryAddress {
    name: string;
    company: string;
    phone: string;
    address: Address;
}

export interface PurchaseOrderJob extends Entity {
    jobNumber: string;
    address: Address;
}

export interface PurchaseOrderSupplier extends Entity {
    site?: SiteTiny;
    contact?: NotifiableContactEntity;
    address: Address;
    phone: string;
    email: string;
    markupPercent?: number;
    contactName?: string; // I think we r not use contactName any more. It is for old purchase-order.
}

export interface PurchaseOrderBillingCompany extends Entity {
    address: Address;
    phone: string;
    email: string;
    taxRate: number;
    taxId: string;
}

export interface PurchaseOrderSummary {
    _id: string;
    poDate: Date;
    poNumber: string;
    billingCompany: Entity;
    supplier: Entity;
    createdBy: Entity;
    total: number;
    subTotal: number;
    job?: PurchaseOrderJob;
    status: PurchaseOrderStatus;
}

export interface PurchaseOrderCreateResponse {
    message?: string;
    accountingId: string;
    deepLink?: string;
    createdDate?: Date;
    success?: boolean;
}

export enum PurchaseOrderCommandId {
    Approve = 'Approve',
    Submit = 'Submit',
    Bill = 'Bill',
    MarkAsSent = 'MarkAsSent',
    SendPo = 'SendPo',
    GenerateBill = 'GenerateBill',
    Cancel = 'Cancel',
    CancelAndCancelLinkedItems = 'CancelAndCancelLinkedItems',
    NewTemplate = 'NewTemplate',
    GetAttachment = 'GetAttachment',
    ChangesRequired = 'ChangesRequired',
}

export type PurchaseOrderCommandStatus = PurchaseOrderCommandId.Approve | PurchaseOrderCommandId.Submit | PurchaseOrderCommandId.Bill | PurchaseOrderCommandId.MarkAsSent | PurchaseOrderCommandId.SendPo | PurchaseOrderCommandId.Cancel;

export type PurchaseOrderCommandRequest =
    {
        commandId: PurchaseOrderCommandId.ChangesRequired;
        data: { _id: string; changesRequiredNote: string };
    } |
    {
        commandId: PurchaseOrderCommandId.CancelAndCancelLinkedItems;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.Approve;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.Submit;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.Bill;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.MarkAsSent;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.SendPo;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.Cancel;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.GenerateBill;
        data: { _id: string };
    } |
    {
        commandId: PurchaseOrderCommandId.NewTemplate;
        // TODO: should use entityId and entityType instead of jobId, workId
        data: { _id: string, jobId?: string, workId?: string, codeIds?: string[] };
    } |
    {
        commandId: PurchaseOrderCommandId.GetAttachment;
        data: { _id: string };
    };
